import {
  Badge,
  Button,
  Card,
  CardHeader,
  CardFooter,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
  DropdownToggle,
  Media,
  Table,
  Container,
  Row,
  Pagination,
  PaginationItem,
  PaginationLink,
  Modal,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Input,
  Label
} from "reactstrap";
import { useEffect, useState, useMemo } from "react";
import Header from "components/Headers/Header.js";
import axios from "axios";
import Loader from "components/Loader";
import { io } from "socket.io-client";

const API_URL = process.env.REACT_APP_API_URL;
const HOST_URL = process.env.REACT_APP_HOST;

const TableDevice = () => {
  const [devices, setDevices] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingStatus, setLoadingStatus] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [currentDevice, setCurrentDevice] = useState(null);
  const [sortPortOrder, setSortPortOrder] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [qrStatus, setQrStatus] = useState("");
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [newDevice, setNewDevice] = useState({
    name: "",
    number: "",
    status: "Inativo",
    countryId: "",
    port: ""
  });

  const fetchDevices = async () => {
    try {
      const response = await axios.get(`${API_URL}/devices`);
      const devicesWithStatus = await Promise.all(
        response.data.map(async (device) => {
          const statusResponse = await axios.get(
            `${API_URL}/bot/status/${device.id}`
          );
          return { ...device, status: statusResponse.data.status };
        })
      );
      setDevices(devicesWithStatus);
    } catch (error) {
      console.error("Erro ao buscar dispositivos:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await axios.get(`${API_URL}/countries`);
      setCountries(response.data);
    } catch (error) {
      console.error("Erro ao buscar países:", error);
    }
  };

  useEffect(() => {
    fetchDevices();
    fetchCountries();
  }, []);

  const sortedDevices = useMemo(() => {
    const sorted = [...devices].sort((a, b) => {
      const portA = Number(a.port) || 0;
      const portB = Number(b.port) || 0;
      return sortPortOrder === "asc" ? portA - portB : portB - portA;
    });

    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sorted.slice(startIndex, endIndex);
  }, [devices, sortPortOrder, currentPage, itemsPerPage]);

  if (loading) {
    return (
      <>
        <Header />
        <Loader />
      </>
    );
  }

  const toggleModal = () => setModalOpen(!modalOpen);
  const toggleEditModal = () => setEditModalOpen(!editModalOpen);

  const handleCreateDevice = async () => {
    const deviceToCreate = {
      ...newDevice,
      countryId: newDevice.countryId || null,
    };
    try {
      const response = await axios.post(`${API_URL}/devices`, deviceToCreate);
      setDevices([...devices, response.data.device]);
      setNewDevice({ name: "", number: "", status: "Inativo", countryId: "", port: "" });
      toggleModal();
    } catch (error) {
      console.error("Erro ao criar dispositivo:", error);
    }
  };

  const handleDeleteDevice = async (id) => {
    try {
      await axios.delete(`${API_URL}/devices/${id}`);
      setDevices(devices.filter((device) => device.id !== id));
    } catch (error) {
      console.error("Erro ao deletar dispositivo:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewDevice({ ...newDevice, [name]: value });
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentDevice({ ...currentDevice, [name]: value });
  };

  const handleSaveChanges = async () => {
    if (!currentDevice) return;

    try {
      await axios.put(`${API_URL}/devices/${currentDevice.id}`, currentDevice);
      setDevices(
        devices.map((device) =>
          device.id === currentDevice.id ? currentDevice : device
        )
      );
      toggleEditModal();
    } catch (error) {
      console.error("Erro ao salvar alterações:", error);
    }
  };

  const handleStartBot = async (index) => {
    const device = devices[index];
    if (!device) return;

    setLoadingStatus(index);

    try {
      await axios.post(`${API_URL}/bot/start/${device.id}`);

      setTimeout(async () => {
        try {
          const statusResponse = await axios.get(
            `${API_URL}/bot/status/${device.id}`
          );
          const updatedStatus = statusResponse.data.status;
          const updatedDevices = [...devices];
          updatedDevices[index] = { ...device, status: updatedStatus };
          setDevices(updatedDevices);
        } catch (statusError) {
          console.error("Erro ao atualizar status do dispositivo:", statusError);
        }
        setLoadingStatus(null);
      }, 5001);
    } catch (error) {
      console.error("Erro ao iniciar o bot:", error);
      setLoadingStatus(null);
    }
  };

  const handleStopBot = async (index) => {
    const device = devices[index];
    if (!device) return;

    setLoadingStatus(index);

    try {
      await axios.post(`${API_URL}/bot/stop/${device.id}`);

      setTimeout(async () => {
        try {
          const statusResponse = await axios.get(
            `${API_URL}/bot/status/${device.id}`
          );
          const updatedStatus = statusResponse.data.status;
          const updatedDevices = [...devices];
          updatedDevices[index] = { ...device, status: updatedStatus };
          setDevices(updatedDevices);
        } catch (statusError) {
          console.error("Erro ao atualizar status do dispositivo:", statusError);
        }
        setLoadingStatus(null);
      }, 5001);
    } catch (error) {
      console.error("Erro ao parar o bot:", error);
      setLoadingStatus(null);
    }
  };

  const handleGenerateQRCode = (device) => {
    if (!device.port) {
      console.warn("Dispositivo sem porta definida:", device);
      return;
    }

    setSelectedDevice(device);
    setQrModalOpen(true);
    setQrStatus('🔄 Conectando ao dispositivo...');
    setQrCodeData(null);

    // Rastrear tempo de abertura do modal
    const modalOpenTime = Date.now();
    const minLoaderTime = 800; // Mínimo 800ms de loader

    // Determinar host correto (usar REACT_APP_HOST ou window.location.hostname)
    const appHost = process.env.REACT_APP_HOST
      ? process.env.REACT_APP_HOST.replace(/\/$/, '') // Remove barra final se existir
      : window.location.protocol + '//' + window.location.hostname;

    // Se appHost já tiver porta (ex: localhost:5001), precisamos removê-la para adicionar a porta do bot
    // Mas se for produção (ex: dominio.com), precisamos manter o protocolo

    // Simplificação: Vamos extrair apenas o hostname
    let hostname;
    try {
      // Tentar criar URL completa se appHost tiver protocolo
      if (appHost.startsWith('http')) {
        const url = new URL(appHost);
        hostname = url.hostname;
      } else {
        // Se for só o domínio/IP
        hostname = appHost.split(':')[0];
      }
    } catch (e) {
      hostname = window.location.hostname;
    }

    // Construir URL do bot - Lógica Simplificada para HTTPS (Polling First)
    let botUrl;
    if (window.location.protocol === 'https:') {
      // Produção: https://meudominio.com (sem porta, proxy reverso)
      botUrl = `https://${hostname}`;
    } else {
      // Local: http://localhost:8001
      botUrl = `http://${hostname}:${device.port}`;
    }

    const botPrefix = `/bot${device.port}`;

    console.log(`🚀 [v3.0 - Polling First] Conectando socket...`);
    console.log(`🔌 URL Base: ${botUrl}`);
    console.log(`🔌 Path: ${botPrefix}/socket.io`);

    // Tentar Polling primeiro (mais robusto com proxies HTTP padrão)
    const socket = io(botUrl, {
      path: `${botPrefix}/socket.io`,
      transports: ['polling', 'websocket'], // Ordem importa: polling primeiro!
      secure: true,
      rejectUnauthorized: false
    });

    socket.on('connect', () => {
      console.log('✅ Socket conectado');
      setQrStatus('📡 Aguardando QR Code...');
    });

    socket.on('qr', (qr) => {
      console.log('📱 QR Code recebido');

      // Calcular tempo decorrido desde abertura do modal
      const elapsedTime = Date.now() - modalOpenTime;
      const remainingTime = Math.max(0, minLoaderTime - elapsedTime);

      console.log(`⏱️ Tempo decorrido: ${elapsedTime}ms, aguardando mais ${remainingTime}ms`);

      // Garantir que loader seja visível por tempo mínimo
      setTimeout(() => {
        setQrCodeData(qr);
        setQrStatus('📱 Escaneie o QR Code com seu WhatsApp');
      }, remainingTime);
    });

    socket.on('message', (msg) => {
      console.log('💬 Mensagem:', msg);
      setQrStatus(msg);

      // Se a mensagem for sobre autenticação, manter o loader visível
      // (não setar qrCodeData, assim o loader continua aparecendo)
      if (msg.includes('Autenticado') || msg.includes('Carregando')) {
        setQrCodeData(null); // Garantir que loader continue visível
      }
    });

    socket.on('ready', () => {
      console.log('✅ Dispositivo conectado!');
      setQrStatus('✅ Conectado com sucesso!');
      setTimeout(() => {
        setQrModalOpen(false);
        socket.disconnect();
        fetchDevices(); // Atualiza lista
      }, 2000);
    });

    socket.on('disconnect', () => {
      console.log('🔌 Socket desconectado');
    });

    socket.on('connect_error', (error) => {
      console.error('❌ Erro de conexão:', error);
      setQrStatus('❌ Erro ao conectar. Verifique se o bot está ativo.');
    });

    // Cleanup quando fechar o modal manualmente
    const cleanup = () => {
      if (socket) {
        socket.disconnect();
      }
    };

    // Armazenar cleanup para usar no toggle do modal
    window.qrSocketCleanup = cleanup;
  };

  const totalPages = Math.ceil(devices.length / itemsPerPage);

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        <Row>
          <div className="col">
            <Card className="shadow">
              <CardHeader className="border-0 d-flex justify-content-between align-items-center">
                <h3 className="mb-0">Lista de Dispositivos</h3>
                <Button color="success" onClick={toggleModal}>
                  Adicionar Novo Dispositivo
                </Button>
              </CardHeader>

              <Table className="align-items-center table-flush" responsive>
                <thead className="thead-light">
                  <tr>
                    <th
                      scope="col"
                      style={{ cursor: "pointer" }}
                      onClick={() => setSortPortOrder(sortPortOrder === "asc" ? "desc" : "asc")}
                    >
                      Porta {sortPortOrder === "asc" ? "▲" : "▼"}
                    </th>
                    <th scope="col">Nome</th>
                    <th scope="col">Número</th>
                    <th scope="col">Status</th>
                    <th scope="col">País</th>
                    <th scope="col" />
                  </tr>
                </thead>
                <tbody>
                  {sortedDevices.map((device, index) => (
                    <tr key={device.id}>
                      <td>{device.port}</td>
                      <th scope="row">
                        <Media className="align-items-center">
                          <span className="mb-0 text-sm">{device.name}</span>
                        </Media>
                      </th>
                      <td>{device.number}</td>
                      <td>
                        {loadingStatus === index ? (
                          <Badge color="" className="badge-dot mr-4">
                            <i className="bg-warning" />
                            Carregando...
                          </Badge>
                        ) : (
                          <Badge color="" className="badge-dot mr-4">
                            <i className={device.status === "Inativo" ? "bg-danger" : "bg-success"} />
                            {device.status}
                          </Badge>
                        )}
                      </td>
                      <td>{device.Country ? device.Country.name : "Não vinculado"}</td>
                      <td className="text-right">
                        <UncontrolledDropdown>
                          <DropdownToggle className="btn-icon-only text-light" href="#pablo" role="button" size="sm" color="" onClick={(e) => e.preventDefault()}>
                            <i className="fas fa-ellipsis-v" />
                          </DropdownToggle>
                          <DropdownMenu container="body" className="dropdown-menu-arrow" right>
                            <DropdownItem onClick={() => handleStartBot(index)}>
                              Ativar Bot
                            </DropdownItem>
                            <DropdownItem onClick={() => handleStopBot(index)}>
                              Desativar Bot
                            </DropdownItem>
                            <DropdownItem onClick={() => handleGenerateQRCode(device)}>
                              Gerar QR Code
                            </DropdownItem>
                            <DropdownItem onClick={() => { setCurrentDevice(device); toggleEditModal(); }}>
                              Editar
                            </DropdownItem>
                            <DropdownItem onClick={() => handleDeleteDevice(device.id)}>
                              Deletar
                            </DropdownItem>
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              <CardFooter className="py-4">
                <Pagination className="pagination justify-content-end mb-0">
                  {[...Array(totalPages)].map((_, pageIndex) => (
                    <PaginationItem key={pageIndex} active={pageIndex + 1 === currentPage}>
                      <PaginationLink href="#p" onClick={(e) => { e.preventDefault(); setCurrentPage(pageIndex + 1); }}>
                        {pageIndex + 1}
                      </PaginationLink>
                    </PaginationItem>
                  ))}
                </Pagination>
              </CardFooter>
            </Card>
          </div>
        </Row>
      </Container>

      {/* Modal de criação */}
      <Modal isOpen={modalOpen} toggle={toggleModal}>
        <div className="modal-header">
          <h5 className="modal-title">Adicionar Novo Dispositivo</h5>
          <button className="close" type="button" onClick={toggleModal}>
            <span aria-hidden={true}>×</span>
          </button>
        </div>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label for="name">Nome</Label>
              <Input type="text" id="name" name="name" value={newDevice.name} onChange={handleInputChange} placeholder="Nome do dispositivo" />
            </FormGroup>
            <FormGroup>
              <Label for="number">Número</Label>
              <Input type="text" id="number" name="number" value={newDevice.number} onChange={handleInputChange} placeholder="Ex: +351912345678" />
            </FormGroup>
            <FormGroup>
              <Label for="countryId">País</Label>
              <Input type="select" id="countryId" name="countryId" value={newDevice.countryId} onChange={handleInputChange}>
                <option value="">Selecione um país</option>
                {countries.map((country) => (
                  <option key={country.id} value={country.id}>
                    {country.name} ({country.code})
                  </option>
                ))}
              </Input>
            </FormGroup>
            <FormGroup>
              <Label for="port">Porta</Label>
              <Input
                type="number"
                id="port"
                name="port"
                value={newDevice.port}
                onChange={handleInputChange}
                placeholder="Ex: 8001"
              />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleCreateDevice}>
            Criar Dispositivo
          </Button>
          <Button color="secondary" onClick={toggleModal}>
            Cancelar
          </Button>
        </ModalFooter>
      </Modal>

      {/* Modal de edição */}
      <Modal isOpen={editModalOpen} toggle={toggleEditModal}>
        <div className="modal-header">
          <h5 className="modal-title">Editar Dispositivo</h5>
          <button className="close" type="button" onClick={toggleEditModal}>
            <span aria-hidden={true}>×</span>
          </button>
        </div>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label for="name">Nome</Label>
              <Input type="text" id="name" name="name" value={currentDevice ? currentDevice.name : ""} onChange={handleEditInputChange} placeholder="Nome do dispositivo" />
            </FormGroup>
            <FormGroup>
              <Label for="number">Número</Label>
              <Input type="text" id="number" name="number" value={currentDevice ? currentDevice.number : ""} onChange={handleEditInputChange} placeholder="Número do dispositivo" />
            </FormGroup>
            <FormGroup>
              <Label for="countryId">País</Label>
              <Input type="select" id="countryId" name="countryId" value={currentDevice ? currentDevice.countryId : ""} onChange={handleEditInputChange}>
                <option value="">Selecione um país</option>
                {countries.map((country) => (
                  <option key={country.id} value={country.id}>
                    {country.name} ({country.code})
                  </option>
                ))}
              </Input>
            </FormGroup>
            <FormGroup>
              <Label for="port">Porta</Label>
              <Input
                type="number"
                id="port"
                name="port"
                value={currentDevice ? currentDevice.port : ""}
                onChange={handleEditInputChange}
                placeholder="Ex: 8001"
              />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleSaveChanges}>
            Salvar Alterações
          </Button>
          <Button color="secondary" onClick={toggleEditModal}>
            Cancelar
          </Button>
        </ModalFooter>
      </Modal>

      {/* Modal de QR Code */}
      <Modal
        isOpen={qrModalOpen}
        toggle={() => {
          setQrModalOpen(false);
          if (window.qrSocketCleanup) {
            window.qrSocketCleanup();
          }
        }}
        size="md"
        centered
      >
        <div className="modal-header">
          <h5 className="modal-title">
            🔗 Conectar Dispositivo: {selectedDevice?.name}
          </h5>
          <button
            className="close"
            type="button"
            onClick={() => {
              setQrModalOpen(false);
              if (window.qrSocketCleanup) {
                window.qrSocketCleanup();
              }
            }}
          >
            <span>×</span>
          </button>
        </div>
        <ModalBody className="text-center py-5">
          {qrCodeData ? (
            <div>
              <img
                src={qrCodeData}
                alt="QR Code WhatsApp"
                style={{
                  width: '100%',
                  maxWidth: '300px',
                  border: '2px solid #25D366',
                  borderRadius: '10px',
                  padding: '10px'
                }}
              />
              <p className="mt-4 mb-0 text-muted">
                <i className="fas fa-mobile-alt mr-2"></i>
                {qrStatus}
              </p>
            </div>
          ) : (
            <div>
              <div className="spinner-border text-success" role="status" style={{ width: '3rem', height: '3rem' }}>
                <span className="sr-only">Carregando...</span>
              </div>
              <p className="mt-4 mb-2 font-weight-bold text-dark">{qrStatus}</p>
              <p className="text-muted small mb-0">
                <i className="fas fa-info-circle mr-1"></i>
                Aguarde enquanto o WhatsApp Web é inicializado...
              </p>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => {
              setQrModalOpen(false);
              if (window.qrSocketCleanup) {
                window.qrSocketCleanup();
              }
            }}
          >
            Fechar
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default TableDevice;